
import { useState } from 'react';
import { useProductModal } from '../../context/ProductModalContext';
import { useCart } from '../../context/CartContext';

const sizes = ['XS', 'S', 'M', 'L', 'XL'];

export default function ProductDetailModal() {
  const { selectedProduct, isModalOpen, closeProductModal } = useProductModal();
  const { addToCart } = useCart();
  const [selectedSize, setSelectedSize] = useState('M');
  const [quantity, setQuantity] = useState(1);

  if (!selectedProduct) return null;

  const discount = selectedProduct.originalPrice
    ? Math.round(((selectedProduct.originalPrice - selectedProduct.price) / selectedProduct.originalPrice) * 100)
    : 0;

  const handleAddToCart = () => {
    addToCart({
      id: selectedProduct.id,
      name: selectedProduct.name,
      price: selectedProduct.price,
      originalPrice: selectedProduct.originalPrice,
      image: selectedProduct.image,
      size: selectedSize
    }, quantity);
    closeProductModal();
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ${isModalOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={closeProductModal}
      />
      <div
        className={`fixed bottom-0 left-0 right-0 bg-white z-50 rounded-t-3xl max-h-[90vh] overflow-y-auto transition-transform duration-300 ease-out ${isModalOpen ? 'translate-y-0' : 'translate-y-full'}`}
      >
        <div className="sticky top-0 bg-white z-10 flex justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
        </div>
        <button
          onClick={closeProductModal}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors cursor-pointer z-20"
        >
          <i className="ri-close-line text-xl text-gray-600"></i>
        </button>

        <div className="px-4 pb-8 md:px-8 md:pb-10">
          <div className="flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="md:w-1/2">
              <div className="relative aspect-[4/5] bg-[#faf8f8] rounded-2xl overflow-hidden">
                <img
                  src={selectedProduct.image}
                  alt={selectedProduct.name}
                  className="w-full h-full object-cover object-top"
                />
                {discount > 0 && (
                  <div className="absolute top-4 right-4 bg-lilac-400 text-white rounded-full py-1.5 px-3">
                    <p className="text-xs font-['Poppins'] font-bold">-{discount}%</p>
                  </div>
                )}
                {selectedProduct.badges && selectedProduct.badges.length > 0 && (
                  <div className="absolute top-4 left-4 flex flex-col gap-2">
                    {selectedProduct.badges.map((badge, index) => (
                      <span
                        key={index}
                        className="bg-lilac-400 text-white text-[10px] font-semibold px-3 py-1 rounded-full uppercase tracking-wide"
                      >
                        {badge}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="md:w-1/2 flex flex-col">
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-gray-800 mb-3">
                {selectedProduct.name}
              </h2>
              
              <div className="flex items-center gap-3 mb-4">
                <span className="font-['Poppins'] text-2xl font-bold text-lilac-400">
                  ₦{selectedProduct.price.toLocaleString()}
                </span>
                {selectedProduct.originalPrice && (
                  <span className="font-['Poppins'] text-lg text-gray-400 line-through">
                    ₦{selectedProduct.originalPrice.toLocaleString()}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className={`ri-star-fill text-sm ${star <= 4 ? 'text-amber-400' : 'text-gray-300'}`}></i>
                ))}
                <span className="text-gray-500 text-sm ml-2 font-['Poppins']">(24 reviews)</span>
              </div>

              <p className="text-gray-600 font-['Poppins'] text-sm leading-relaxed mb-6">
                {selectedProduct.description || "No description available for this product."}
              </p>

              <div className="mb-6">
                <p className="font-['Poppins'] font-medium text-gray-800 mb-3">Select Size</p>
                <div className="flex flex-wrap gap-2">
                  {sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`w-12 h-12 rounded-lg font-['Poppins'] font-medium text-sm transition-all cursor-pointer ${
                        selectedSize === size
                          ? 'bg-lilac-400 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <p className="font-['Poppins'] font-medium text-gray-800 mb-3">Quantity</p>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-subtract-line text-lg"></i>
                  </button>
                  <span className="font-['Poppins'] font-medium text-lg w-8 text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line text-lg"></i>
                  </button>
                </div>
              </div>

              <div className="flex gap-3 mt-auto">
                <button className="w-14 h-14 flex items-center justify-center rounded-xl border-2 border-gray-200 hover:border-lilac-400 hover:text-lilac-400 transition-colors cursor-pointer">
                  <i className="ri-heart-line text-xl"></i>
                </button>
                <button
                  onClick={handleAddToCart}
                  className="flex-1 h-14 bg-lilac-400 text-white rounded-xl font-['Poppins'] font-semibold hover:bg-lilac-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Add to Cart - ₦{(selectedProduct.price * quantity).toLocaleString()}
                </button>
              </div>

              <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-gray-100">
                <div className="flex items-center gap-2 text-gray-500">
                  <i className="ri-truck-line text-lg"></i>
                  <span className="text-xs font-['Poppins']">6 Days Delivery</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <i className="ri-refresh-line text-lg"></i>
                  <span className="text-xs font-['Poppins']">Easy Returns</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <i className="ri-shield-check-line text-lg"></i>
                  <span className="text-xs font-['Poppins']">Secure</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
